
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';

/**
 * SwapsExportResponse struct
 */
class SwapsExportResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!string=} filePath
   * @constructor
   */
  constructor (parent = new Response(), filePath = '') {
    super()
    super.copy(parent)
    this.FilePath = filePath
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SwapsExportResponse}
   * @param {!SwapsExportResponse} other Other struct
   * @returns {!SwapsExportResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.FilePath != null) {
      this.FilePath = other.FilePath
    } else {
      this.FilePath = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SwapsExportResponse}
   * @returns {!SwapsExportResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SwapsExportResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SwapsExportResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SwapsExportResponse}
   * @param {!SwapsExportResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SwapsExportResponse)) {
      throw new TypeError('Instance of SwapsExportResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SwapsExportResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      FilePath: ((this.FilePath != null) ? this.FilePath : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SwapsExportResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SwapsExportResponse} other Object value
   * @returns {!SwapsExportResponse} Created struct
   */
  static fromObject (other) {
    return new SwapsExportResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SwapsExportResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SwapsExportResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SwapsExportResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6119
  }
}

export { SwapsExportResponse };

/**
 * Fast Binary Encoding SwapsExportResponse field model
 */
class FieldModelSwapsExportResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.FilePath = new fbe.FieldModelString(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelSwapsExportResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelSwapsExportResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSwapsExportResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.FilePath.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSwapsExportResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.FilePath.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSwapsExportResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSwapsExportResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSwapsExportResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6119
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSwapsExportResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSwapsExportResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.FilePath.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FilePath.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FilePath.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSwapsExportResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSwapsExportResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSwapsExportResponse}
   * @param {!SwapsExportResponse} fbeValue Default value, defaults is new SwapsExportResponse()
   * @returns {!SwapsExportResponse} SwapsExportResponse value
   */
  get (fbeValue = new SwapsExportResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSwapsExportResponse}
   * @param {!SwapsExportResponse} fbeValue SwapsExportResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.FilePath.fbeSize) <= fbeStructSize) {
      fbeValue.FilePath = this.FilePath.get()
    } else {
      fbeValue.FilePath = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FilePath.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSwapsExportResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSwapsExportResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSwapsExportResponse}
   * @param {!SwapsExportResponse} fbeValue SwapsExportResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSwapsExportResponse}
   * @param {SwapsExportResponse} fbeValue SwapsExportResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.FilePath.set(fbeValue.FilePath)
  }
}

export { FieldModelSwapsExportResponse };

/**
 * Fast Binary Encoding SwapsExportResponse model
 */
class SwapsExportResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSwapsExportResponse(this.buffer, 4)
  }

  /**
   * Get the SwapsExportResponse model
   * @this {!SwapsExportResponseModel}
   * @returns {!FieldModelSwapsExportResponse} model SwapsExportResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SwapsExportResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SwapsExportResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SwapsExportResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SwapsExportResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSwapsExportResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SwapsExportResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SwapsExportResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SwapsExportResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SwapsExportResponseModel}
   * @param {!SwapsExportResponse} value SwapsExportResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SwapsExportResponseModel}
   * @param {!SwapsExportResponse} value SwapsExportResponse value, defaults is new SwapsExportResponse()
   * @return {!object} Deserialized SwapsExportResponse value and its size
   */
  deserialize (value = new SwapsExportResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SwapsExportResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SwapsExportResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SwapsExportResponseModel}
   * @param {!number} prev Previous SwapsExportResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SwapsExportResponseModel };
